/* eslint-disable max-lines */
import {
  type CursorEntity,
  selectCursor,
  setCursor,
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from '@salescore/client-redux'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  convertRecordNodeToInnerCells,
  convertRecordNodeToSheetRow,
  type SheetCell,
  type UiColumn,
  type ViewQueryRecordNode,
} from '@salescore/core'
import { App } from 'antd'
import { Array, HashMap, Number, Option, Order, pipe } from 'effect'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'
import { z } from 'zod'

import { useVmid } from '../components/VmidContext'
import { buildColumnWidths, type RSheetColumn, type RSheetRecordNode } from '../domain/column'
import { type EdgeType, TOP_LEFT_CURSOR_POSITION } from '../domain/cursor/actions/getNextCursor'
import { bindCursorActions } from '../domain/cursor/cursorActions'
import { validateCursor } from '../domain/cursor/cursorSelectors'
import type { RSheetsCursor, RSheetsCursorPosition } from '../domain/cursor/cursorTypes'
import { useGetRowHeight } from '../rsheet/recoil/selectors/rowsRelatedSelector'
import { useCalculatedRowHeight } from './navigation/hooks'
import { useRecordsImpl } from './records'
import { type RowConfig, rowConfigsImplAtom } from './rowConfig'
import type { SalescoreObjectAbility, ViewAbility } from './view/atoms'

/* eslint-disable sonarjs/todo-tag */
/**
 * TODO: この hook は store.getState() で呼び出し時点の cursor snapshot を取得する。
 * subscription しないためリアクティブではなく、バグの温床になりうる。
 * コンポーネント設計を見直し、useAppSelector で subscribe する形に
 * 移行した上で、この hook は削除すること。
 */
/* eslint-enable sonarjs/todo-tag */
export const useGetCursorSnapshot = (): (() => CursorEntity | undefined) => {
  const store = useAppStore()
  const vmid = useVmid()
  return useCallback((): CursorEntity | undefined => store.getState().views.viewStates[vmid]?.cursor, [store, vmid])
}

export const useCursorModel = (columns: Array<RSheetColumn<RSheetRecordNode>>): RSheetsCursor | undefined => {
  const vmid = useVmid()
  const cursor = useAppSelector((state) => selectCursor(state, vmid))
  const rows = useRecordsImpl()

  return useMemo(() => pipe(cursor, validateCursor(rows, columns), Option.getOrUndefined), [columns, cursor, rows])
}

export const useSetCursorModel = (
  ability: ViewAbility & SalescoreObjectAbility,
): ((
  newCursorOrUpdater:
    (RSheetsCursor | undefined) | ((previous: RSheetsCursor | undefined) => RSheetsCursor | undefined),
) => void) => {
  const vmid = useVmid()
  const { message } = App.useApp()
  const getCursorSnapshot = useGetCursorSnapshot()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  return useCallback(
    (newCursorOrUpdater) => {
      const previous = getCursorSnapshot()
      const newCursor = typeof newCursorOrUpdater === 'function' ? newCursorOrUpdater(previous) : newCursorOrUpdater

      if (!ability.canSaveRecord && newCursor?.editing?.isEditing === true && !ability.canEditSalescoreObjectRecord) {
        // eslint-disable-next-line sonarjs/todo-tag
        // TODO: feedbackMessagesAtomの名残でエラー時のフィードバックの責務が混入していて良くない
        message.warning(t(`このシートのレコードを編集する権限がありません`))
        // 権限がない場合は変更を適用せず現在の状態を維持
        return
      }
      if (newCursor !== undefined && newCursor.editing?.isEditing === true && newCursor.copying?.visible === true) {
        // isEditingがtrueになった時、copying.visibleを強制的にfalseにする
        dispatch(
          setCursor({
            vmid,
            cursor: {
              ...newCursor,
              copying: {
                ...newCursor.copying,
                visible: false,
              },
            },
          }),
        )
        return
      }
      dispatch(setCursor({ vmid, cursor: newCursor }))
    },
    [ability.canEditSalescoreObjectRecord, ability.canSaveRecord, dispatch, getCursorSnapshot, message, t, vmid],
  )
}

interface CursorSelectorResult {
  position?: CursorPosition
  expandArea?: ExpandArea
  selectedArea?: ExpandArea
  copiedArea?: ExpandArea
  fixedLeftColumnIndex?: number
  fixedLeftColumnWidth?: number
}

export const useGetCursorSelector = (
  fixedLeftColumnIndex: number,
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  // eslint-disable-next-line sonarjs/use-type-alias
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  visibleRowNumber: number | undefined,
): ((cursor: RSheetsCursor | undefined) => CursorSelectorResult) => {
  const rows = useRecordsImpl()
  const { getRowHeightWithRowIndex } = useGetRowHeight(visibleRowNumber)
  const calculatedRowHeight = useCalculatedRowHeight(compiledConfig)
  const rowConfigMap = useRecoilValue(rowConfigsImplAtom)
  const getInnerCellsWithRowIndex = useCallback(
    (rowIndex: number, column: RSheetColumn<RSheetRecordNode>) =>
      pipe(
        rows,
        Array.get(rowIndex),
        Option.map((row) => getInnerCellsByColumn(row, [column], rowConfigMap, visibleRowNumber)),
        Option.getOrElse(() => []),
      ),
    [rowConfigMap, rows, visibleRowNumber],
  )
  const getVisibleHeightWithRowIndex = useGetVisibleHeightWithRowIndex(columns, visibleRowNumber)

  return useCallback(
    (cursor) => {
      if (cursor === undefined) {
        return {}
      }
      const widths = buildColumnWidths(columns)
      const column = columns[cursor.main.columnIndex]
      const row = rows[cursor.main.rowIndex]
      if (column === undefined || row === undefined) {
        return {}
      }

      const fixedLeftColumnWidth = Number.sumAll(widths.slice(0, fixedLeftColumnIndex))

      const expandCursorAreaParameters = {
        widths,
        fixedLeftColumnIndex,
        rowHeight: calculatedRowHeight,
        columns,
        getRowHeightWithRowIndex,
        getInnerCellsWithRowIndex,
        getVisibleHeightWithRowIndex,
      }

      return {
        position: calculateMainCursorArea({
          column,
          widths,
          mainCursor: cursor.main,
          rowHeight: calculatedRowHeight,
          getRowHeightWithRowIndex,
          getInnerCellsWithRowIndex,
          getVisibleHeightWithRowIndex,
        }),
        // Shift選択をした範囲範囲（＝expandがない場合は空になりうる）
        expandArea: calculateExpandCursorArea({
          ...expandCursorAreaParameters,
          mainCursor: cursor.main,
          expandCursor: cursor.expand,
        }),
        // Shift選択、クリック選択の両方を含む範囲（＝直感的な意味での「選択範囲」）
        selectedArea: calculateExpandCursorArea({
          ...expandCursorAreaParameters,
          mainCursor: cursor.main,
          expandCursor: cursor.expand ?? cursor.main,
        }),
        copiedArea: calculateExpandCursorArea({
          ...expandCursorAreaParameters,
          mainCursor: cursor.copying?.main,
          expandCursor: cursor.copying?.expand ?? cursor.copying?.main,
        }),
        fixedLeftColumnIndex,
        fixedLeftColumnWidth,
      }
    },
    [
      calculatedRowHeight,
      columns,
      fixedLeftColumnIndex,
      getInnerCellsWithRowIndex,
      getRowHeightWithRowIndex,
      getVisibleHeightWithRowIndex,
      rows,
    ],
  )
}

export const useCursorSelector = (
  fixedLeftColumnIndex: number,
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  visibleRowNumber: number | undefined,
): CursorSelectorResult => {
  const getCursorSelector = useGetCursorSelector(fixedLeftColumnIndex, columns, compiledConfig, visibleRowNumber)
  const cursor = useCursorModel(columns)

  return useMemo(() => getCursorSelector(cursor), [cursor, getCursorSelector])
}

/**
 * innerCells を可視行数の上限に合わせて切り詰める。
 * 各セルの高さを先頭から順に再計算し、可視範囲に1行以上かかるセルのみ返す。
 */
const truncateInnerCells = (innerCells: SheetCell[], visibleRowNumber: number): SheetCell[] =>
  pipe(
    innerCells,
    Array.mapAccum(0, (previousHeight, cell) => {
      const height = forceBetween(visibleRowNumber - previousHeight, 0, cell.height)

      return [
        previousHeight + cell.height,
        height > 0 ? Option.some({ ...cell, height }) : Option.none<SheetCell>(),
      ] as const
    }),
    ([, mapped]) => mapped,
    Array.getSomes,
  )

export const useRowExpandable = (
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  visibleRowNumber: number | undefined,
): ((row: ViewQueryRecordNode) => boolean) => {
  const uiColumns = useMemo(
    () =>
      columns.map((column): UiColumn => ({
        fieldName: column.field?.name,
        nodePath: column.node.path,
      })),
    [columns],
  )

  return useCallback(
    (row: ViewQueryRecordNode): boolean => {
      const sheetRow = convertRecordNodeToSheetRow(row, uiColumns)

      return visibleRowNumber !== undefined && sheetRow.height > visibleRowNumber
    },
    [uiColumns, visibleRowNumber],
  )
}

type GetRowHeightWithRowIndex = (rowIndex: number) => number
type GetInnerCells = (row: ViewQueryRecordNode, column: RSheetColumn<RSheetRecordNode>) => SheetCell[]
type GetInnerCellsWithRowIndex = (rowIndex: number, column: RSheetColumn<RSheetRecordNode>) => SheetCell[]
type GetVisibleHeightWithRowIndex = (rowIndex: number) => number

const getInnerCellsByColumn = (
  row: ViewQueryRecordNode,
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  rowConfigMap: HashMap.HashMap<string, RowConfig>,
  visibleRowNumber: number | undefined,
): SheetCell[] => {
  const { id: rowId } = row
  if (rowId === undefined) return []

  const expanded = pipe(
    rowConfigMap,
    HashMap.get(rowId),
    Option.map((rowConfig) => rowConfig.expanded),
    Option.getOrElse(() => false),
  )

  return pipe(
    columns,
    Array.map((column) => ({ fieldName: column.field?.name, nodePath: column.node.path })),
    Array.map((column) => convertRecordNodeToInnerCells(row, column)),
    Array.flatMap((innerCells) => {
      const showingAllRows = visibleRowNumber === undefined || expanded
      return showingAllRows ? innerCells : truncateInnerCells(innerCells, visibleRowNumber)
    }),
  )
}

export const useGetInnerCells = (visibleRowNumber: number | undefined): GetInnerCells => {
  const rowConfigMap = useRecoilValue(rowConfigsImplAtom)

  return useCallback(
    (row: ViewQueryRecordNode, column: RSheetColumn<RSheetRecordNode>) =>
      getInnerCellsByColumn(row, [column], rowConfigMap, visibleRowNumber),
    [rowConfigMap, visibleRowNumber],
  )
}

export const useGetVisibleHeightWithRowIndex = (
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  visibleRowNumber: number | undefined,
): GetVisibleHeightWithRowIndex => {
  const rows = useRecordsImpl()
  const rowConfigMap = useRecoilValue(rowConfigsImplAtom)

  return useCallback(
    (rowIndex: number): number =>
      pipe(
        rows,
        Array.get(rowIndex),
        Option.map((row: ViewQueryRecordNode): number => {
          const innerCells = getInnerCellsByColumn(row, columns, rowConfigMap, visibleRowNumber)
          return innerCells.length === 0 ? 1 : Math.max(...innerCells.map((innerCell) => innerCell.height))
        }),
        Option.getOrElse(() => 0),
      ),
    [columns, rowConfigMap, rows, visibleRowNumber],
  )
}

export interface CursorPosition {
  x: number
  y: number
  width: number
  height: number
}

interface CalculateMainCursorAreaParameters {
  column: RSheetColumn<RSheetRecordNode>
  widths: number[]
  mainCursor: RSheetsCursorPosition
  rowHeight: number
  getRowHeightWithRowIndex: GetRowHeightWithRowIndex
  getInnerCellsWithRowIndex: GetInnerCellsWithRowIndex
  getVisibleHeightWithRowIndex: GetVisibleHeightWithRowIndex
}

export function calculateMainCursorArea({
  column,
  widths,
  mainCursor,
  rowHeight,
  getRowHeightWithRowIndex,
  getInnerCellsWithRowIndex,
  getVisibleHeightWithRowIndex,
}: CalculateMainCursorAreaParameters): CursorPosition | undefined {
  const currentInnerCell = getInnerRecordNodeWithPreviousAndNext(
    getInnerCellsWithRowIndex(mainCursor.rowIndex, column),
    mainCursor.innerRowIndex,
  ).current

  const area: ExpandCursorArea = {
    leftColumnIndex: mainCursor.columnIndex,
    rightColumnIndex: mainCursor.columnIndex,
    upInnerRowIndex: currentInnerCell?.innerRowIndexStart ?? mainCursor.innerRowIndex,
    downInnerRowIndex: currentInnerCell?.innerRowIndexEnd ?? mainCursor.innerRowIndex,
    upRowIndex: mainCursor.rowIndex,
    downRowIndex: mainCursor.rowIndex,
  }

  return convertArea2Position({ area, widths, getRowHeightWithRowIndex, rowHeight, getVisibleHeightWithRowIndex })
}

export const useCalculatePosition = (
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  visibleRowNumber: number | undefined,
) => {
  const columnWidths = buildColumnWidths(columns)

  const { getRowHeightWithRowIndex } = useGetRowHeight(visibleRowNumber)
  const calculatedRowHeight = useCalculatedRowHeight(compiledConfig)
  const getVisibleHeightWithRowIndex = useGetVisibleHeightWithRowIndex(columns, visibleRowNumber)

  return useCallback(
    (cursor: RSheetsCursorPosition): CursorPosition | undefined => {
      const area: ExpandCursorArea = {
        leftColumnIndex: cursor.columnIndex,
        rightColumnIndex: cursor.columnIndex,
        upInnerRowIndex: cursor.innerRowIndex,
        downInnerRowIndex: cursor.innerRowIndex,
        upRowIndex: cursor.rowIndex,
        downRowIndex: cursor.rowIndex,
      }

      return convertArea2Position({
        area,
        widths: columnWidths,
        getRowHeightWithRowIndex,
        rowHeight: calculatedRowHeight,
        getVisibleHeightWithRowIndex,
      })
    },
    [calculatedRowHeight, columnWidths, getRowHeightWithRowIndex, getVisibleHeightWithRowIndex],
  )
}

interface ConvertArea2PositionParameters {
  area: ExpandCursorArea
  widths: number[]
  rowHeight: number
  getRowHeightWithRowIndex: GetRowHeightWithRowIndex
  getVisibleHeightWithRowIndex: GetVisibleHeightWithRowIndex
}

const convertArea2Position = ({
  area,
  widths,
  rowHeight,
  getRowHeightWithRowIndex,
  getVisibleHeightWithRowIndex,
}: ConvertArea2PositionParameters): CursorPosition => {
  const x = Number.sumAll(widths.slice(0, area.leftColumnIndex)) // 0からleftまで
  const width = Number.sumAll(widths.slice(area.leftColumnIndex, area.rightColumnIndex + 1)) // leftからrightまで
  const y =
    Number.sumAll([
      ...(area.upRowIndex <= 0 ? [] : pipe(Array.makeBy(area.upRowIndex, getRowHeightWithRowIndex))),
      area.upInnerRowIndex,
    ]) * rowHeight
  const heights = calculateRowHeights(area, getVisibleHeightWithRowIndex)
  const height = Number.sumAll(heights) * rowHeight

  return {
    x,
    y,
    width,
    height,
  }
}

export interface ExpandArea {
  area: ExpandCursorArea
  x: number
  y: number
  width: number
  height: number
  fixedRightWidth: number
}

interface CalculateExpandCursorAreaParameters {
  widths: number[]
  mainCursor: RSheetsCursorPosition | undefined
  expandCursor: RSheetsCursorPosition | undefined
  fixedLeftColumnIndex: number
  rowHeight: number
  columns: Array<RSheetColumn<RSheetRecordNode>>
  getRowHeightWithRowIndex: GetRowHeightWithRowIndex
  getInnerCellsWithRowIndex: GetInnerCellsWithRowIndex
  getVisibleHeightWithRowIndex: GetVisibleHeightWithRowIndex
}

const isBlankFillerCell = (cell: SheetCell): boolean =>
  cell.recordNode === undefined && cell.parentRecordNode === undefined

const getNavigableInnerCellsForKeyboard = (innerCells: SheetCell[]): SheetCell[] =>
  innerCells.filter((cell) => !isBlankFillerCell(cell))

export function calculateExpandCursorArea({
  widths,
  mainCursor,
  expandCursor,
  fixedLeftColumnIndex,
  rowHeight,
  columns,
  getRowHeightWithRowIndex,
  getInnerCellsWithRowIndex,
  getVisibleHeightWithRowIndex,
}: CalculateExpandCursorAreaParameters): ExpandArea | undefined {
  if (mainCursor === undefined) {
    return
  }

  if (expandCursor === undefined) {
    return
  }

  const area = getExpandCursorArea({
    positionA: mainCursor,
    positionB: expandCursor,
    columns,
    getInnerCellsWithRowIndex,
  })
  const fixedRightIndex = Math.min(fixedLeftColumnIndex, area.rightColumnIndex + 1)
  const fixedRightWidth = Number.sumAll(widths.slice(area.leftColumnIndex, fixedRightIndex))

  return {
    area,
    fixedRightWidth,
    ...convertArea2Position({ area, widths, getRowHeightWithRowIndex, rowHeight, getVisibleHeightWithRowIndex }),
  }
}

interface GetExpandCursorAreaParameters {
  positionA: RSheetsCursor['main']
  positionB: RSheetsCursor['main']
  columns: Array<RSheetColumn<RSheetRecordNode>>
  getInnerCellsWithRowIndex: GetInnerCellsWithRowIndex
}

function getExpandCursorArea({
  positionA,
  positionB,
  columns,
  getInnerCellsWithRowIndex,
}: GetExpandCursorAreaParameters): ExpandCursorArea {
  // 上(下)にあるカーソルを求める（行が同じならinnerRowIndexで比較、行が異なる場合はrowIndex）
  const verticalPositionOrder: Order.Order<RSheetsCursor['main']> = Order.Struct({
    rowIndex: Order.Number,
    innerRowIndex: Order.Number,
  })
  const upperCursor = Order.min(verticalPositionOrder)(positionA, positionB)
  const downerCursor = Order.max(verticalPositionOrder)(positionA, positionB)
  // 左右のカラムのインデックスを求める
  const leftColumnIndex = Math.min(positionA.columnIndex, positionB.columnIndex)
  const rightColumnIndex = Math.max(positionA.columnIndex, positionB.columnIndex)

  // 求めるべきareaは、単純なup-downの領域ではなく、up-downの領域が含むセルが含む領域となる
  // よって、最上段(最下段)のレコードを求める
  const currentColumns = columns.slice(leftColumnIndex, rightColumnIndex + 1)
  const getRecordNodesByCursor = (position: RSheetsCursorPosition): Array<SheetCell | undefined> => {
    const recordNodes = pipe(
      currentColumns,
      Array.map((column) => getInnerCellsWithRowIndex(position.rowIndex, column)),
      Array.map((innerCells) => getInnerRecordNodeWithPreviousAndNext(innerCells, position.innerRowIndex).current),
    )
    return recordNodes
  }
  const upperRecordNodes = getRecordNodesByCursor(upperCursor)
  const downerRecordNodes = getRecordNodesByCursor(downerCursor)

  return {
    leftColumnIndex,
    rightColumnIndex,
    upInnerRowIndex:
      // eslint-disable-next-line sonarjs/todo-tag
      upperRecordNodes.length === 0 ? 0 : Math.min(...upperRecordNodes.map((x) => x?.innerRowIndexStart ?? 0)), // TODO: 存在しないことはありうる？
    downInnerRowIndex: Math.max(0, ...downerRecordNodes.map((x) => x?.innerRowIndexEnd ?? 0)), // endのindexは、次のセルのindex。
    upRowIndex: upperCursor.rowIndex,
    downRowIndex: downerCursor.rowIndex,
  }
}

export const expandCursorAreaSchema = z.object({
  leftColumnIndex: z.number(),
  rightColumnIndex: z.number(),
  upInnerRowIndex: z.number(),
  downInnerRowIndex: z.number(),
  upRowIndex: z.number(),
  downRowIndex: z.number(),
})
export type ExpandCursorArea = z.infer<typeof expandCursorAreaSchema>

const calculateRowHeights = (
  area: ExpandCursorArea,
  getVisibleHeightWithRowIndex: GetVisibleHeightWithRowIndex,
): number[] =>
  pipe(
    Array.range(area.upRowIndex, area.downRowIndex),
    Array.map((rowIndex) => {
      const visibleHeight = getVisibleHeightWithRowIndex(rowIndex)

      const upInnerRowIndex = rowIndex === area.upRowIndex ? area.upInnerRowIndex : 0
      const downInnerRowIndex = rowIndex === area.downRowIndex ? area.downInnerRowIndex : visibleHeight
      // down-upで、upからdownの上までの行の高さ（ただし、最低でも1、かつ表示制限しているときは高さを超えないようにする）
      return forceBetween(downInnerRowIndex - upInnerRowIndex, 1, visibleHeight)
    }),
  )

export const useMoveToEdge = (
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  ability: ViewAbility & SalescoreObjectAbility,
): (({ type, expand }: { type: EdgeType; expand: boolean }) => void) => {
  const { moveToEdge } = useCursorMutation(ability)
  const rows = useRecordsImpl()
  return useCallback(
    ({ type, expand }: { type: EdgeType; expand: boolean }) => {
      moveToEdge({ type, expand, rows, columns })
    },
    [columns, moveToEdge, rows],
  )
}

export const moveCursorImpl = ({
  oldCursor,
  expand,
  columns,
  rows,
  rowDiff,
  columnDiff,
  selectedArea,
  getInnerCells,
}: {
  oldCursor: RSheetsCursor | undefined
  expand: boolean
  columns: Array<RSheetColumn<RSheetRecordNode>>
  rows: ViewQueryRecordNode[]
  rowDiff: number
  columnDiff: number
  selectedArea: ExpandArea | undefined
  getInnerCells: GetInnerCells
}): RSheetsCursor => {
  // cursorがまだなかったら先頭とする
  if (oldCursor === undefined) {
    return {
      main: TOP_LEFT_CURSOR_POSITION,
    }
  }

  const currentPosition = expand ? (oldCursor.expand ?? oldCursor.main) : oldCursor.main
  const movedPosition = movePosition({
    columns,
    rows,
    currentPosition,
    rowDiff,
    columnDiff,
    ignoreBlankFillerCellForMove: !expand,
    selectedArea: expand ? selectedArea : undefined,
    getInnerCells,
  })
  return expand
    ? {
        main: oldCursor.main,
        expand: movedPosition,
        copying: oldCursor.copying,
      }
    : {
        main: movedPosition,
        copying: oldCursor.copying,
      }
}

export const useSetMoveCursor = (
  fixedLeftColumnIndex: number,
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  ability: ViewAbility & SalescoreObjectAbility,
  visibleRowNumber: number | undefined,
  // eslint-disable-next-line @typescript-eslint/max-params
): (({ expand, columnDiff, rowDiff }: { expand: boolean; columnDiff: number; rowDiff: number }) => void) => {
  const rows = useRecordsImpl()
  const getCursorSelector = useGetCursorSelector(fixedLeftColumnIndex, columns, compiledConfig, visibleRowNumber)
  const getInnerCells = useGetInnerCells(visibleRowNumber)
  const setCursorModel = useSetCursorModel(ability)

  return useCallback(
    ({ expand, rowDiff, columnDiff }: { expand: boolean; columnDiff: number; rowDiff: number }): void => {
      setCursorModel((oldCursor) => {
        const { selectedArea } = getCursorSelector(oldCursor)
        return moveCursorImpl({
          oldCursor,
          expand,
          columns,
          rows,
          rowDiff,
          columnDiff,
          selectedArea,
          getInnerCells,
        })
      })
    },
    [columns, getCursorSelector, getInnerCells, rows, setCursorModel],
  )
}

function findMaxHeightColumnIndex({
  selectedArea,
  row,
  columns,
  getInnerCells,
  innerRowIndex,
}: {
  selectedArea: ExpandArea
  row: RSheetRecordNode
  columns: Array<RSheetColumn<RSheetRecordNode>>
  getInnerCells: GetInnerCells
  innerRowIndex: number
}): number | undefined {
  const { leftColumnIndex, rightColumnIndex } = selectedArea.area
  // 既に選択中の範囲が複数のブロックにまたがっているときの縦移動は、現在と同じ行にある最大高の列での移動のように扱う必要がある
  // この挙動のため、「現在と同じ行にある最大高の列」を求める必要がある
  const recordNodes = columns
    .slice(leftColumnIndex, rightColumnIndex + 1)
    .map((column) => getInnerRecordNodeWithPreviousAndNext(getInnerCells(row, column), innerRowIndex).current)
  // 最大の高さを持つ列を求める
  const maxHeight = Math.max(0, ...recordNodes.map((x) => x?.height ?? 0))
  const maxHeightColumnIndex = recordNodes.findIndex((x) => (x?.height ?? 0) === maxHeight)
  // findIndexで必ず見つかりそうだが、元のコード通り-1の考慮を残しておく
  return maxHeightColumnIndex === -1 ? undefined : maxHeightColumnIndex + leftColumnIndex
}

function movePosition({
  columns,
  rows,
  currentPosition,
  columnDiff,
  rowDiff,
  ignoreBlankFillerCellForMove,
  selectedArea,
  getInnerCells,
}: {
  columns: Array<RSheetColumn<RSheetRecordNode>>
  rows: RSheetRecordNode[]
  currentPosition: RSheetsCursorPosition
  columnDiff: number
  rowDiff: number
  ignoreBlankFillerCellForMove: boolean
  selectedArea?: ExpandArea
  getInnerCells: GetInnerCells
}): RSheetsCursorPosition {
  const maxRowIndex = rows.length - 1
  const maxColumnIndex = columns.length - 1
  const row = rows[currentPosition.rowIndex]
  const column = columns[currentPosition.columnIndex]

  // ありえないはずだが、現在指しているセルがない場合はとりあえず左上をセットする
  if (row === undefined || column === undefined) {
    return TOP_LEFT_CURSOR_POSITION
  }

  // エッジケース以外の移動先
  const movedDefault = {
    rowIndex: forceBetween(currentPosition.rowIndex + rowDiff, TOP_LEFT_CURSOR_POSITION.rowIndex, maxRowIndex),
    columnIndex: forceBetween(
      currentPosition.columnIndex + columnDiff,
      TOP_LEFT_CURSOR_POSITION.columnIndex,
      maxColumnIndex,
    ),
    innerRowIndex: 0, // ネストは別判定とするので、いったん0
  }

  if (rowDiff !== 0) {
    return movePositionVertically({
      row,
      columns,
      rows,
      currentPosition,
      column,
      columnDiff,
      rowDiff,
      movedDefault,
      maxRowIndex,
      selectedArea,
      getInnerCells,
      ignoreBlankFillerCellForMove,
    })
  }

  if (columnDiff !== 0) {
    return movePositionHorizontally({
      row,
      columns,
      currentPosition,
      columnDiff,
      movedDefault,
      ignoreBlankFillerCellForMove,
      getInnerCells,
    })
  }

  return movedDefault
}

function movePositionVertically({
  row,
  columns,
  rows,
  currentPosition,
  column,
  columnDiff,
  rowDiff,
  movedDefault,
  maxRowIndex,
  selectedArea,
  getInnerCells,
  ignoreBlankFillerCellForMove,
}: {
  row: RSheetRecordNode
  columns: Array<RSheetColumn<RSheetRecordNode>>
  rows: RSheetRecordNode[]
  currentPosition: RSheetsCursorPosition
  column: RSheetColumn<RSheetRecordNode>
  columnDiff: number
  rowDiff: number
  movedDefault: RSheetsCursorPosition
  maxRowIndex: number
  selectedArea?: ExpandArea
  getInnerCells: GetInnerCells
  ignoreBlankFillerCellForMove: boolean
}): RSheetsCursorPosition {
  const ignoreBlankFillerCellForVerticalMove = ignoreBlankFillerCellForMove

  if (column.node.path.length <= 1) {
    return movePositionVerticallyForFlatColumn({
      rows,
      column,
      currentPosition,
      rowDiff,
      movedDefault,
      maxRowIndex,
      getInnerCells,
      ignoreBlankFillerCellForMove,
    })
  }

  const maxHeightColumnIndex =
    selectedArea === undefined
      ? undefined
      : findMaxHeightColumnIndex({
          selectedArea,
          row,
          columns,
          getInnerCells,
          innerRowIndex: currentPosition.innerRowIndex,
        })
  if (maxHeightColumnIndex !== undefined) {
    const movedPositionAsMaxHeightColumn = movePosition({
      columns,
      rows,
      currentPosition: {
        ...currentPosition,
        columnIndex: maxHeightColumnIndex,
      },
      columnDiff,
      rowDiff,
      ignoreBlankFillerCellForMove: ignoreBlankFillerCellForVerticalMove,
      getInnerCells,
    })
    const movedPosition = {
      ...movedPositionAsMaxHeightColumn,
      columnIndex: currentPosition.columnIndex,
    }

    if (isMovedTowardDirection(currentPosition, movedPosition, rowDiff)) {
      return movedPosition
    }

    if (selectedArea === undefined) {
      return movedPosition
    }

    const movedAcrossSelectedArea = moveVerticallyAcrossSelectedArea({
      rows,
      columns,
      currentPosition,
      rowDiff,
      selectedArea,
      getInnerCells,
    })

    return movedAcrossSelectedArea ?? currentPosition
  }

  const innerCells = getInnerCells(row, column)
  const { prev, next } = getInnerRecordNodeWithPreviousAndNext(innerCells, currentPosition.innerRowIndex, {
    ignoreBlankFillerCell: ignoreBlankFillerCellForVerticalMove,
  })

  const { innerDestination, dy } = rowDiff < 0 ? { innerDestination: prev, dy: -1 } : { innerDestination: next, dy: 1 }
  if (innerDestination !== undefined) {
    return {
      ...currentPosition,
      innerRowIndex: innerDestination.innerRowIndexStart,
    }
  }

  const moved = verticalMoveRecursively({
    rows,
    column,
    rowIndex: currentPosition.rowIndex,
    dy,
    ignoreBlankFillerCell: ignoreBlankFillerCellForVerticalMove,
    getInnerCells,
  })
  if (moved === undefined) {
    return currentPosition
  }

  return {
    ...currentPosition,
    rowIndex: moved.rowIndex,
    innerRowIndex: moved.innerRowIndex,
  }
}

function isMovedTowardDirection(
  currentPosition: RSheetsCursorPosition,
  movedPosition: RSheetsCursorPosition,
  rowDiff: number,
): boolean {
  if (rowDiff < 0) {
    return (
      movedPosition.rowIndex < currentPosition.rowIndex ||
      (movedPosition.rowIndex === currentPosition.rowIndex &&
        movedPosition.innerRowIndex < currentPosition.innerRowIndex)
    )
  }

  if (rowDiff > 0) {
    return (
      movedPosition.rowIndex > currentPosition.rowIndex ||
      (movedPosition.rowIndex === currentPosition.rowIndex &&
        movedPosition.innerRowIndex > currentPosition.innerRowIndex)
    )
  }

  return false
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function moveVerticallyAcrossSelectedArea({
  rows,
  columns,
  currentPosition,
  rowDiff,
  selectedArea,
  getInnerCells,
}: {
  rows: RSheetRecordNode[]
  columns: Array<RSheetColumn<RSheetRecordNode>>
  currentPosition: RSheetsCursorPosition
  rowDiff: number
  selectedArea: ExpandArea
  getInnerCells: GetInnerCells
}): RSheetsCursorPosition | undefined {
  const maxRowIndex = rows.length - 1
  const dy = rowDiff < 0 ? -1 : 1

  for (
    let movedRowIndex = currentPosition.rowIndex + dy;
    TOP_LEFT_CURSOR_POSITION.rowIndex <= movedRowIndex && movedRowIndex <= maxRowIndex;
    movedRowIndex += dy
  ) {
    const movedRow = pipe(rows, Array.get(movedRowIndex), Option.getOrUndefined)
    if (movedRow === undefined) {
      break
    }

    for (
      let columnIndex = selectedArea.area.leftColumnIndex;
      columnIndex <= selectedArea.area.rightColumnIndex;
      columnIndex += 1
    ) {
      const column = pipe(columns, Array.get(columnIndex), Option.getOrUndefined)
      if (column === undefined) {
        break
      }

      const navigableInnerCells = getNavigableInnerCellsForKeyboard(getInnerCells(movedRow, column))
      if (navigableInnerCells.length === 0) {
        continue
      }

      return {
        ...currentPosition,
        rowIndex: movedRowIndex,
        innerRowIndex:
          dy > 0
            ? (Array.head(navigableInnerCells).pipe(Option.getOrUndefined)?.innerRowIndexStart ?? 0)
            : (Array.last(navigableInnerCells).pipe(Option.getOrUndefined)?.innerRowIndexStart ?? 0),
      }
    }
  }

  return undefined
}

function movePositionVerticallyForFlatColumn({
  rows,
  column,
  currentPosition,
  rowDiff,
  movedDefault,
  maxRowIndex,
  getInnerCells,
  ignoreBlankFillerCellForMove,
}: {
  rows: RSheetRecordNode[]
  column: RSheetColumn<RSheetRecordNode>
  currentPosition: RSheetsCursorPosition
  rowDiff: number
  movedDefault: RSheetsCursorPosition
  maxRowIndex: number
  getInnerCells: GetInnerCells
  ignoreBlankFillerCellForMove: boolean
}): RSheetsCursorPosition {
  if (!ignoreBlankFillerCellForMove) {
    return movedDefault
  }

  const dy = rowDiff < 0 ? -1 : 1
  for (
    let movedRowIndex = currentPosition.rowIndex + dy;
    TOP_LEFT_CURSOR_POSITION.rowIndex <= movedRowIndex && movedRowIndex <= maxRowIndex;
    movedRowIndex += dy
  ) {
    const movedRow = pipe(rows, Array.get(movedRowIndex), Option.getOrUndefined)
    if (movedRow === undefined) {
      break
    }
    const movedInnerCells = getInnerCells(movedRow, column)
    if (movedInnerCells.length === 0) {
      return movedDefault
    }

    const navigableInnerCells = getNavigableInnerCellsForKeyboard(movedInnerCells)
    if (navigableInnerCells.length === 0) {
      continue
    }

    return {
      ...currentPosition,
      rowIndex: movedRowIndex,
      innerRowIndex:
        dy > 0
          ? (Array.head(navigableInnerCells).pipe(Option.getOrUndefined)?.innerRowIndexStart ?? 0)
          : (Array.last(navigableInnerCells).pipe(Option.getOrUndefined)?.innerRowIndexStart ?? 0),
    }
  }

  return currentPosition
}

function movePositionHorizontally({
  row,
  columns,
  currentPosition,
  columnDiff,
  movedDefault,
  ignoreBlankFillerCellForMove,
  getInnerCells,
}: {
  row: RSheetRecordNode
  columns: Array<RSheetColumn<RSheetRecordNode>>
  currentPosition: RSheetsCursorPosition
  columnDiff: number
  movedDefault: RSheetsCursorPosition
  ignoreBlankFillerCellForMove: boolean
  getInnerCells: GetInnerCells
}): RSheetsCursorPosition {
  const moved = horizontalMoveRecursively({
    row,
    columns,
    columnIndex: currentPosition.columnIndex,
    innerRowIndex: currentPosition.innerRowIndex,
    dx: columnDiff,
    ignoreBlankFillerCell: ignoreBlankFillerCellForMove,
    getInnerCells,
  })
  if (moved === undefined) {
    return currentPosition
  }

  return {
    ...movedDefault,
    columnIndex: moved.columnIndex,
    innerRowIndex: moved.innerRowIndex,
  }
}

// eslint-disable-next-line sonarjs/todo-tag
// TODO: nodeUtil, innerRowRelatedにも同じようなメソッドがある
export function getInnerRecordNodeWithPreviousAndNext(
  innerCells: SheetCell[],
  innerRowIndex: number,
  options?: { ignoreBlankFillerCell?: boolean },
): {
  prev: SheetCell | undefined
  current: SheetCell | undefined
  next: SheetCell | undefined
} {
  const targetInnerCells =
    options?.ignoreBlankFillerCell === true ? getNavigableInnerCellsForKeyboard(innerCells) : innerCells
  const currentIndex = targetInnerCells.findIndex(
    (x) => x.innerRowIndexStart <= innerRowIndex && innerRowIndex < x.innerRowIndexEnd,
  )
  const current = pipe(targetInnerCells, Array.get(currentIndex), Option.getOrUndefined)
  const previous = pipe(targetInnerCells, Array.get(currentIndex - 1), Option.getOrUndefined)
  const next = pipe(targetInnerCells, Array.get(currentIndex + 1), Option.getOrUndefined)

  return {
    prev: previous,
    current,
    next,
  }
}

function verticalMoveRecursively({
  column,
  dy,
  rowIndex,
  rows,
  ignoreBlankFillerCell,
  getInnerCells,
}: {
  rows: RSheetRecordNode[]
  column: RSheetColumn<RSheetRecordNode>
  rowIndex: number
  dy: number
  ignoreBlankFillerCell: boolean
  getInnerCells: GetInnerCells
}):
  | undefined
  | {
      rowIndex: number
      innerRowIndex: number
    } {
  const movedRowIndex = rowIndex + dy
  const previousRootRecordNode = pipe(rows, Array.get(movedRowIndex), Option.getOrUndefined)
  if (previousRootRecordNode === undefined) {
    // ここに入るのは以下いずれかのケースのみ
    // ①最上段の行で上方向へ移動
    // ②最下段の行で下方向へ移動
    return undefined
  }
  const previousRecordNodesWithIndex = ignoreBlankFillerCell
    ? getNavigableInnerCellsForKeyboard(getInnerCells(previousRootRecordNode, column))
    : getInnerCells(previousRootRecordNode, column)
  return Option.match(dy > 0 ? Array.head(previousRecordNodesWithIndex) : Array.last(previousRecordNodesWithIndex), {
    onSome: (recordNode) => ({ rowIndex: movedRowIndex, innerRowIndex: recordNode.innerRowIndexStart }),
    onNone: () =>
      verticalMoveRecursively({
        rows,
        column,
        rowIndex: movedRowIndex,
        dy,
        ignoreBlankFillerCell,
        getInnerCells,
      }),
  })
}

function horizontalMoveRecursively({
  row,
  columnIndex,
  columns,
  dx,
  ignoreBlankFillerCell,
  getInnerCells,
  innerRowIndex,
}: {
  row: RSheetRecordNode
  columns: Array<RSheetColumn<RSheetRecordNode>>
  columnIndex: number
  innerRowIndex: number
  dx: number
  ignoreBlankFillerCell: boolean
  getInnerCells: GetInnerCells
}):
  | undefined
  | {
      columnIndex: number
      innerRowIndex: number
    } {
  const movedColumnIndex = columnIndex + dx
  const nextColumn = pipe(columns, Array.get(movedColumnIndex), Option.getOrUndefined)
  if (nextColumn === undefined || movedColumnIndex < TOP_LEFT_CURSOR_POSITION.columnIndex) {
    // ここに入るのは以下いずれかのケースのみ
    // ①左端の列で左方向へ移動 （※行番号列（rowIndex）には移動不可のためTOP_LEFT_CURSOR_POSITIONと比較している）
    // ②右端の列で右方向へ移動
    return undefined
  }
  if (nextColumn.node.path.length <= 1) {
    if (ignoreBlankFillerCell) {
      const movedInnerCells = getInnerCells(row, nextColumn)
      if (movedInnerCells.length > 0) {
        const recordNodesWithIndex = getNavigableInnerCellsForKeyboard(movedInnerCells)
        if (recordNodesWithIndex.length === 0) {
          return horizontalMoveRecursively({
            row,
            columns,
            columnIndex: movedColumnIndex,
            innerRowIndex,
            dx,
            ignoreBlankFillerCell,
            getInnerCells,
          })
        }
      }
    }

    return {
      columnIndex: movedColumnIndex,
      innerRowIndex: 0,
    }
  }

  // 次のカラムがネストしたカラムのとき

  const recordNodesWithIndex = ignoreBlankFillerCell
    ? getNavigableInnerCellsForKeyboard(getInnerCells(row, nextColumn))
    : getInnerCells(row, nextColumn)
  // 空の孫など、セルが一切存在しない場合は、更に次の列へ移動
  if (recordNodesWithIndex.length === 0) {
    return horizontalMoveRecursively({
      row,
      columns,
      columnIndex: movedColumnIndex,
      innerRowIndex,
      dx,
      ignoreBlankFillerCell,
      getInnerCells,
    })
  }
  const innerCell = getInnerRecordNodeWithPreviousAndNext(recordNodesWithIndex, innerRowIndex).current
  // ある場合は、適した列に移動
  return {
    columnIndex: movedColumnIndex,
    innerRowIndex:
      innerCell?.innerRowIndexStart ??
      (dx > 0
        ? (Array.head(recordNodesWithIndex).pipe(Option.getOrUndefined)?.innerRowIndexStart ?? 0)
        : (Array.last(recordNodesWithIndex).pipe(Option.getOrUndefined)?.innerRowIndexStart ?? 0)),
  }
}

function forceBetween(x: number, min: number, max: number): number {
  return Math.max(Math.min(x, max), min)
}

export const useCursorMutation = (
  ability: ViewAbility & SalescoreObjectAbility,
): ReturnType<typeof bindCursorActions> => {
  // ここでcursorを呼ぶと、useCursorMutationを呼んでいる箇所でcursorの変更のたびにレンダリングが走るので注意
  const setCursorModel = useSetCursorModel(ability)

  // 関数生成時に計算コストがかかっている。セルの中で使用されると馬鹿にならないので適切な形に書き直してから戻す
  // const moveCursor = useSetMoveCursor()

  return {
    // setCursor: setCursorModel,
    ...useMemo(() => bindCursorActions(setCursorModel), [setCursorModel]),
    // moveCursor,
  }
}

export const useMoveToRowIndexAndEditModeMutation = (
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  ability: ViewAbility & SalescoreObjectAbility,
) => {
  const { moveToRowIndexAndEditMode } = useCursorMutation(ability)

  return useCallback(
    (rowIndex: number): void => {
      moveToRowIndexAndEditMode(rowIndex, columns)
    },
    [moveToRowIndexAndEditMode, columns],
  )
}
